import type { BugsWrapper } from '../bugs/Bugs';
import { error, type HttpError } from '@sveltejs/kit';
import { GraphQLClient } from 'graphql-request';
import type { ZodError } from 'zod';
import { fromZodError } from 'zod-validation-error';
import type { Query } from './Query';
import { PUBLIC_ENV_MODE } from '$env/static/public';

export interface DatoRequestResponse<T> {
	success: boolean;
	error?: HttpError;
	data: T;
}

interface RequestResponseError {
	response: {
		errors?: [];
		status: number;
		headers: { [key: string]: string };
	};
	request: {
		query: string;
	};
}

export class DatoCMSGraphQL {
	private readonly _client: GraphQLClient;
	private readonly _bugReporter?: BugsWrapper;

	constructor(
		token: string,
		options?: { includeDrafts?: boolean; excludeInvalid?: boolean },
		bugReporter?: BugsWrapper
	) {
		this._bugReporter = bugReporter;
		const headers: Record<string, string> = {
			authorization: `Bearer ${token}`
		};
		if (options?.includeDrafts) headers['X-Include-Drafts'] = 'true';
		if (options?.excludeInvalid) headers['X-Exclude-Invalid'] = 'true';

		this._client = new GraphQLClient('https://graphql.datocms.com', { headers });
	}

	/**
	 * @param query A graphql query for fetching data from DatoCMS
	 * @param options Additional options for the request and response
	 * @param options.variables Variables to be passed to the query
	 * @param options.useValidationFilter If true, the response will be filtered to only include the fields specified in the query zod validation. Defaults to true.
	 * @returns A promise that resolves to a DatoRequestResponse
	 * @example
	 * ```typescript
	 * const result = await cms.request( query );
	 * if (result.success) {
	 * 	console.log(result.data);
	 * } else {
	 * 	console.error(result.error);
	 * }
	 * ```
	 **/
	public async request<T = unknown>(
		query: Query<T>,
		options?: { variables?: Record<string, unknown> }
	): Promise<DatoRequestResponse<T>> {
		const result: DatoRequestResponse<T> = {
			success: false,
			data: {} as T,
			error: undefined
		};
		try {
			const response = await this._client.request<T>(query.query, options?.variables);
			if (response) {
				result.success = true;
				result.data = response;
			}
		} catch (e) {
			console.error(JSON.stringify(e, null, 2));
			const message = 'Failed to fetch data from CMS';
			const errors = (e as RequestResponseError).response?.errors ?? [];
			const advancedMessage =
				message + `: \n` + errors.map((error: { message: string }) => error.message).join(', \n');

			this._bugReporter?.reportError(new Error(advancedMessage), {
				query
			});
			result.error = error(422, PUBLIC_ENV_MODE !== 'production' ? advancedMessage : message);
			return result;
		}

		if (query?.validation) {
			const validatedItems = await query.validation.safeParseAsync(result.data);
			result.success = validatedItems.success;
			if (validatedItems.success) {
				result.data = validatedItems.data;
			} else {
				result.error = error(422, fromZodError(validatedItems.error as ZodError).toString());
			}
		} else {
			result.success = true;
		}

		return result;
	}
}
